import { generateNKeysBetween } from 'fractional-indexing';
import { BOARD_FILE_LIMITS, BoardFileError, type BoardFile, type BoardFileAsset } from '../board-file';
import { newId } from '../ids';
import { BOARD_LIMITS } from '../limits';
import { DEFAULT_BACKGROUND, DEFAULT_GRID } from '../meta';
import { PAGE_LIMIT, PAGE_MAIN, PAGE_NAME_MAX } from '../pages';
import { SCHEMA_VERSION, TABLE_MAX_COLS, TABLE_MAX_ROWS, TABLE_ROW_H, type ConnectorEnd, type ObjectData, type ObjectType } from '../schema';
import { TABLE_MAX_COL_W, TABLE_MIN_COL_W } from '../table';
import type { FigmaEndpoint, FigmaNode, FigmaPaint, FigmaRect, FigmaSource } from './types';

/** Images per import (spec §0: a board holds up to ~300 images); the next ones go to the report. */
export const FIGMA_IMAGE_LIMIT = BOARD_LIMITS.images;

export type FigmaIgnored = { id: string; name: string; type: string; reason: 'unsupported' | 'limit' | 'hidden' | 'approximate' };
export type FigmaImportReport = {
  pages: number;
  imported: Partial<Record<ObjectType, number>>;
  /** Nodes left out, and nodes imported only approximately (`approximate`). */
  ignored: FigmaIgnored[];
  images: { total: number; withoutUrl: number };
};

export type FigmaColors = {
  /** The studio sticky colour nearest to a FigJam sticky's fill (`null`: no solid fill). */
  stickyColor(hex: string | null): string;
  sectionFill(hex: string | null): string;
  exportedAt: string;
};

const SHAPES: Record<string, Extract<ObjectData, { type: 'shape' }>['shape']> = {
  SQUARE: 'rectangle', ROUNDED_RECTANGLE: 'rectangle', ELLIPSE: 'ellipse', DIAMOND: 'diamond', TRIANGLE_DOWN: 'triangle', STAR: 'star', SPEECH_BUBBLE: 'bubble',
};
// TRIANGLE_DOWN points down in FigJam and up here.
const EXACT_SHAPES = new Set(['SQUARE', 'ROUNDED_RECTANGLE', 'ELLIPSE', 'DIAMOND', 'STAR', 'SPEECH_BUBBLE']);
const ROUTING: Record<string, Extract<ObjectData, { type: 'connector' }>['routing']> = { STRAIGHT: 'straight', ELBOWED: 'elbow', CURVED: 'curved' };
const ARROW_CAPS = new Set(['LINE_ARROW', 'TRIANGLE_ARROW', 'TRIANGLE_FILLED']);
const MAGNETS: Record<string, { nx: number; ny: number }> = { TOP: { nx: 0.5, ny: 0 }, BOTTOM: { nx: 0.5, ny: 1 }, LEFT: { nx: 0, ny: 0.5 }, RIGHT: { nx: 1, ny: 0.5 } };
const LABEL_MAX = 200;

const str = (v: unknown, max = 10_000): string => (typeof v === 'string' ? v.slice(0, max) : '');
const fin = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const isNode = (v: unknown): v is FigmaNode => typeof v === 'object' && v !== null && !Array.isArray(v);
const kids = (n: FigmaNode): FigmaNode[] => (Array.isArray(n.children) ? n.children.filter(isNode) : []);

function boxOf(n: FigmaNode): FigmaRect | null {
  const b = n.absoluteBoundingBox;
  if (!b || !fin(b.x) || !fin(b.y) || !fin(b.width) || !fin(b.height) || b.width < 0 || b.height < 0) return null;
  return { x: b.x, y: b.y, width: b.width, height: b.height };
}

const visiblePaints = (p: unknown): FigmaPaint[] => (Array.isArray(p) ? p.filter((x): x is FigmaPaint => isNode(x) && x.visible !== false) : []);

/** `#rrggbb` of the first visible solid paint. */
function solidHex(p: unknown): string | null {
  const c = visiblePaints(p).find((x) => x.type === 'SOLID')?.color;
  if (!c || !fin(c.r) || !fin(c.g) || !fin(c.b)) return null;
  const h = (v: number) => Math.round(clamp01(v) * 255).toString(16).padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

const imageRefOf = (n: FigmaNode): string | null => {
  const ref = visiblePaints(n.fills).find((x) => x.type === 'IMAGE')?.imageRef;
  return typeof ref === 'string' && ref.length > 0 && ref.length <= 200 ? ref : null;
};

type Base = Pick<ObjectData, 'id' | 'parentId' | 'x' | 'y' | 'w' | 'h' | 'rotation' | 'z' | 'locked' | 'style'> & { pageId: string };

/**
 * A FigJam file read by the REST API as a `BoardFile` (backlog §12, plan P11b Ruling 8, spike P11b-figma.md): each
 * CANVAS is a page centred on the origin, nodes keep their size, paint order and nesting (children placed relative to
 * their section or group), and every node left out or only approximated is listed in the report. Pure: ids are new,
 * image assets point at the download URLs Figma gave.
 */
export function figmaToBoardFile(input: FigmaSource, colors: FigmaColors): { file: BoardFile; report: FigmaImportReport } {
  const report: FigmaImportReport = { pages: 0, imported: {}, ignored: [], images: { total: 0, withoutUrl: 0 } };
  const objects: ObjectData[] = [];
  const pages: BoardFile['pages'] = [];
  const assets = new Map<string, BoardFileAsset>();
  // Figma id → our id, and every node's absolute box (also of ignored nodes, for connector ends).
  const ids = new Map<string, string>();
  const boxes = new Map<string, FigmaRect>();
  const connectors: { o: Extract<ObjectData, { type: 'connector' }>; n: FigmaNode }[] = [];
  let images = 0;

  const ignore = (n: FigmaNode, reason: FigmaIgnored['reason']) =>
    report.ignored.push({ id: str(n.id, 64), name: str(n.name, 200), type: str(n.type, 64) || 'UNKNOWN', reason });
  const push = (o: ObjectData) => {
    if (objects.length >= BOARD_FILE_LIMITS.objects) throw new BoardFileError('too_large');
    objects.push(o);
    report.imported[o.type] = (report.imported[o.type] ?? 0) + 1;
  };

  const walk = (n: FigmaNode, parent: { id: string; x: number; y: number } | null, pageId: string) => {
    const box = boxOf(n);
    const fid = str(n.id, 200);
    if (fid && box) boxes.set(fid, box);
    if (n.visible === false) return ignore(n, 'hidden');
    const type = str(n.type, 64);
    const ref = type === 'STICKY' || type === 'SHAPE_WITH_TEXT' || type === 'TEXT' ? null : imageRefOf(n);
    const container = type === 'SECTION' || type === 'GROUP' || (type === 'FRAME' && !ref);
    const known = ref !== null || container || ['STICKY', 'SHAPE_WITH_TEXT', 'TEXT', 'CONNECTOR', 'LINE', 'TABLE'].includes(type);
    if (!known || (!box && type !== 'CONNECTOR')) return ignore(n, 'unsupported');
    if (ref && ++images > FIGMA_IMAGE_LIMIT) return ignore(n, 'limit');
    const id = newId();
    if (fid) ids.set(fid, id);
    const b = box ?? { x: 0, y: 0, width: 1, height: 1 };
    const base: Base = {
      id, parentId: parent?.id ?? null, x: b.x - (parent?.x ?? 0), y: b.y - (parent?.y ?? 0), w: b.width, h: b.height,
      rotation: 0, z: '', locked: false, style: {}, pageId,
    };
    // Without `size` (geometry=paths only) a rotated node cannot be rebuilt: it stays in its bounding box.
    if (fin(n.rotation) && Math.abs(n.rotation) > 1e-6) ignore(n, 'approximate');
    const text = str(n.characters);
    const fill = solidHex(n.fills);
    const stroke = solidHex(n.strokes);
    if (ref) {
      if (!assets.has(ref)) {
        const url = input.images[ref];
        // Any http(s) URL: the Worker's relay decides which hosts it fetches (plan P11b Ruling 5).
        const ok = typeof url === 'string' && /^https?:\/\//.test(url) && url.length <= 2048;
        if (!ok) report.images.withoutUrl++;
        assets.set(ref, { id: newId(), sha256: null, mime: 'image/png', w: 0, h: 0, url: ok ? url : null, file: null });
      }
      push({ ...base, type: 'image', assetId: assets.get(ref)!.id, crop: { x: 0, y: 0, w: 1, h: 1 }, alt: str(n.name, 500) });
      // A frame with an image fill keeps its children, placed where they were.
      for (const k of kids(n)) walk(k, parent, pageId);
      return;
    }
    switch (type) {
      case 'STICKY':
        return push({ ...base, type: 'sticky', text, color: colors.stickyColor(fill) });
      case 'SHAPE_WITH_TEXT': {
        const shapeType = str(n.shapeType, 64);
        if (!EXACT_SHAPES.has(shapeType) && !(fin(n.rotation) && Math.abs(n.rotation) > 1e-6)) ignore(n, 'approximate');
        return push({ ...base, style: { ...(fill ? { fill } : {}), ...(stroke ? { stroke } : {}) }, type: 'shape', shape: SHAPES[shapeType] ?? 'rectangle', text });
      }
      case 'TEXT': {
        const size = n.style?.fontSize;
        return push({ ...base, style: fill ? { color: fill } : {}, type: 'text', text, fontSize: fin(size) ? Math.min(400, Math.max(6, size)) : 16 });
      }
      case 'LINE':
        return push({ ...base, style: stroke ? { stroke } : {}, type: 'arrow', points: [{ x: 0, y: 0 }, { x: b.width, y: b.height }] });
      case 'TABLE': {
        const table = tableOf(n, base);
        if (table) return push(table);
        ids.delete(fid);
        return ignore(n, 'unsupported');
      }
      case 'CONNECTOR': {
        const free = { objectId: null, anchor: null, x: 0, y: 0 };
        const startCap = str(n.connectorStartStrokeCap, 64);
        const endCap = str(n.connectorEndStrokeCap, 64);
        const label = text.trim() ? text.slice(0, LABEL_MAX) : undefined;
        const approx = [startCap, endCap].some((c) => c && c !== 'NONE' && !ARROW_CAPS.has(c)) || text.length > LABEL_MAX;
        if (approx && !(fin(n.rotation) && Math.abs(n.rotation) > 1e-6)) ignore(n, 'approximate');
        const o: Extract<ObjectData, { type: 'connector' }> = {
          ...base, parentId: null, x: 0, y: 0, w: 1, h: 1, style: stroke ? { stroke } : {}, type: 'connector', start: free, end: free,
          routing: ROUTING[str(n.connectorLineType, 64)] ?? 'straight',
          startCap: startCap && startCap !== 'NONE' ? 'arrow' : 'none', endCap: endCap && endCap !== 'NONE' ? 'arrow' : 'none',
          ...(label ? { label } : {}),
        };
        connectors.push({ o, n });
        return push(o);
      }
      default: {
        // SECTION, FRAME, GROUP.
        const kind = type === 'GROUP' ? { type: 'group' as const } : { type: 'section' as const, name: str(n.name, 200), fill: colors.sectionFill(fill), clip: false, collapsed: false };
        push({ ...base, ...kind } as ObjectData);
        for (const k of kids(n)) walk(k, { id, x: b.x, y: b.y }, pageId);
      }
    }
  };

  const canvases = kids(input.document).filter((n) => n.type === 'CANVAS');
  canvases.forEach((c, i) => {
    if (i >= PAGE_LIMIT) return ignore(c, 'limit');
    const pageId = i === 0 ? PAGE_MAIN : newId();
    pages.push({ id: pageId, name: str(c.name, PAGE_NAME_MAX) });
    for (const k of kids(c)) walk(k, null, pageId);
  });
  if (pages.length === 0) pages.push({ id: PAGE_MAIN, name: '' });
  report.pages = pages.length;

  // Connector ends, once every node has its id and box.
  const ourBox = (id: string | undefined) => (id && ids.has(id) ? boxes.get(id) : undefined);
  for (const { o, n } of connectors) {
    const s = endOf(n.connectorStart, n.connectorEnd);
    const e = endOf(n.connectorEnd, n.connectorStart);
    if (!s || !e) {
      objects.splice(objects.indexOf(o), 1);
      report.imported.connector!--;
      ignore(n, 'unsupported');
      continue;
    }
    o.start = s;
    o.end = e;
  }
  function pointOf(ep: FigmaEndpoint | undefined): { x: number; y: number } | null {
    if (!ep) return null;
    if (ep.position && fin(ep.position.x) && fin(ep.position.y)) return { x: ep.position.x, y: ep.position.y };
    const b = ep.endpointNodeId ? boxes.get(ep.endpointNodeId) : undefined;
    return b ? { x: b.x + b.width / 2, y: b.y + b.height / 2 } : null;
  }
  function endOf(ep: FigmaEndpoint | undefined, other: FigmaEndpoint | undefined): ConnectorEnd | null {
    if (!isNode(ep)) return null;
    const b = ourBox(ep.endpointNodeId);
    if (b && b.width > 0 && b.height > 0) {
      let anchor = MAGNETS[str(ep.magnet, 16)];
      if (!anchor && ep.position && fin(ep.position.x) && fin(ep.position.y))
        anchor = { nx: clamp01((ep.position.x - b.x) / b.width), ny: clamp01((ep.position.y - b.y) / b.height) };
      if (!anchor) {
        // AUTO: the side facing the other end.
        const to = pointOf(other) ?? { x: b.x + b.width / 2, y: b.y + b.height / 2 };
        const dx = (to.x - (b.x + b.width / 2)) / b.width;
        const dy = (to.y - (b.y + b.height / 2)) / b.height;
        anchor = Math.abs(dx) > Math.abs(dy) ? MAGNETS[dx > 0 ? 'RIGHT' : 'LEFT']! : MAGNETS[dy > 0 ? 'BOTTOM' : 'TOP']!;
      }
      return { objectId: ids.get(ep.endpointNodeId!)!, anchor, x: b.x + anchor.nx * b.width, y: b.y + anchor.ny * b.height };
    }
    const p = pointOf(ep);
    return p ? { objectId: null, anchor: null, x: p.x, y: p.y } : null;
  }

  // Each page centred on the origin: top-level objects and connector ends move, children follow their parent.
  for (const p of pages) {
    const top = objects.filter((o) => o.pageId === p.id && o.parentId === null && o.type !== 'connector');
    if (!top.length) continue;
    const minX = Math.min(...top.map((o) => o.x));
    const minY = Math.min(...top.map((o) => o.y));
    const maxX = Math.max(...top.map((o) => o.x + o.w));
    const maxY = Math.max(...top.map((o) => o.y + o.h));
    const dx = -(minX + maxX) / 2;
    const dy = -(minY + maxY) / 2;
    for (const o of objects) {
      if (o.pageId !== p.id) continue;
      if (o.type === 'connector') {
        o.start = { ...o.start, x: o.start.x + dx, y: o.start.y + dy };
        o.end = { ...o.end, x: o.end.x + dx, y: o.end.y + dy };
      } else if (o.parentId === null) {
        o.x += dx;
        o.y += dy;
      }
    }
  }

  const keys = generateNKeysBetween(null, null, objects.length);
  objects.forEach((o, i) => (o.z = keys[i]!));
  report.images.total = assets.size;
  return {
    file: {
      format: 'os-board', version: 2, schemaVersion: SCHEMA_VERSION, exportedAt: colors.exportedAt, title: str(input.name, 200),
      meta: { background: DEFAULT_BACKGROUND, grid: DEFAULT_GRID }, pages, objects, assets: [...assets.values()],
    },
    report,
  };
}

/** A TABLE from its TABLE_CELL children (row then column order, spike): columns and rows from the cells' boxes. */
function tableOf(n: FigmaNode, base: Base): Extract<ObjectData, { type: 'table' }> | null {
  const cells = kids(n)
    .filter((c) => c.type === 'TABLE_CELL')
    .flatMap((c) => {
      const b = boxOf(c);
      return b ? [{ b, text: str(c.characters, 5000) }] : [];
    });
  if (!cells.length) return null;
  const xs = [...new Set(cells.map((c) => Math.round(c.b.x)))].sort((a, b) => a - b).slice(0, TABLE_MAX_COLS);
  const ys = [...new Set(cells.map((c) => Math.round(c.b.y)))].sort((a, b) => a - b).slice(0, TABLE_MAX_ROWS);
  const cols = xs.map((x) => {
    const w = cells.find((c) => Math.round(c.b.x) === x)!.b.width;
    return { id: newId(), w: Math.min(TABLE_MAX_COL_W, Math.max(TABLE_MIN_COL_W, w)) };
  });
  const rows = ys.map((y) => ({
    id: newId(),
    cells: Object.fromEntries(cols.map((col, i) => [col.id, cells.find((c) => Math.round(c.b.y) === y && Math.round(c.b.x) === xs[i])?.text ?? ''])),
  }));
  return { ...base, type: 'table', w: cols.reduce((s, c) => s + c.w, 0), h: rows.length * TABLE_ROW_H, cols, rows };
}
